"use client";

import { useEffect, useState } from "react";
// Import SVGs as image URLs instead of components
import AppStoreEnImg from "./assets/appStoreEn.svg";
import AppStoreFrImg from "./assets/appStoreFr.svg";
import AppStoreNlImg from "./assets/appStoreNl.svg";
import PlayStoreEnImg from "./assets/playStoreEn.svg";
import PlayStoreFrImg from "./assets/playStoreFr.svg";
import PlayStoreNlImg from "./assets/playStoreNl.svg";
import styles from "./RedirectToStore.module.scss";

export default function RedirectToStore({
  isMobile,
  language = "fr",
  onContinueWeb,
  appType = "DEFAULT",
}) {
  const [redirectFailed, setRedirectFailed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const androidUrl =
    "https://play.google.com/store/apps/details?id=be.tamtam.unitedassociates";
  const iosUrl = "https://apps.apple.com/app/United-Associates/id6744465237";

  // Get store image URLs based on language
  const getAppStoreImage = () => {
    switch (language) {
      case "nl":
        return AppStoreNlImg;
      case "en":
        return AppStoreEnImg;
      case "fr":
      default:
        return AppStoreFrImg;
    }
  };

  const translate = (key) => {
    const translations = {
      fr: {
        "common.titreDownloadApp": "Téléchargez notre application",
        "common.redirectionToStoreMessage":
          "Pour une meilleure expérience, installez notre application mobile",
        "common.continueOnWeb": "Continuer sur le web",
        "common.returnToWebsite": "Retour au site web",
        "common.closeModal": "Fermer",
      },
      en: {
        "common.titreDownloadApp": "Download our app",
        "common.redirectionToStoreMessage":
          "For a better experience, install our mobile app",
        "common.continueOnWeb": "Continue on the web",
        "common.returnToWebsite": "Return to website",
        "common.closeModal": "Close",
      },
      nl: {
        "common.titreDownloadApp": "Download onze app",
        "common.redirectionToStoreMessage":
          "Voor een betere ervaring, installeer onze mobiele app",
        "common.continueOnWeb": "Doorgaan op het web",
        "common.returnToWebsite": "Terug naar website",
        "common.closeModal": "Sluiten",
      },
    };

    return translations[language]?.[key] || key;
  };

  const getPlayStoreImage = () => {
    switch (language) {
      case "nl":
        return PlayStoreNlImg;
      case "en":
        return PlayStoreEnImg;
      case "fr":
      default:
        return PlayStoreFrImg;
    }
  };

  // Check if user has already chosen to continue on web

  // Redirect to stores
  useEffect(() => {
    if (isMobile && showPrompt) {
      const isAndroid = /android/i.test(navigator.userAgent);
      const isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent);

      if (isAndroid) {
        try {
          window.location.href = "market://details?id=com.tamtam.offfcourse";

          setTimeout(() => {
            if (document.visibilityState !== "hidden") {
              setRedirectFailed(true);
            }
          }, 2000);
        } catch (e) {
          setRedirectFailed(true);
        }
      } else if (isIOS) {
        try {
          window.location.href = iosUrl;

          setTimeout(() => {
            if (document.visibilityState !== "hidden") {
              setRedirectFailed(true);
            }
          }, 2000);
        } catch (e) {
          setRedirectFailed(true);
        }
      } else {
        setRedirectFailed(true);
      }
    }
  }, [isMobile, showPrompt]);

  // Handle continue on web choice
  const handleContinueOnWeb = () => {
    onContinueWeb();
  };

  // Handle modal close
  const handleClose = () => {
    if (onContinueWeb) {
      onContinueWeb();
    }
  };

  // Don't show anything if prompt is disabled
  if (!showPrompt) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        {redirectFailed ? (
          <div className={styles.modalContent}>
            <button onClick={handleClose} className={styles.closeButton}>
              ×
            </button>

            <h2 className={styles.modalTitle}>
              {translate("common.titreDownloadApp")}
            </h2>
            <p className={styles.modalMessage}>
              {translate("common.redirectionToStoreMessage")}
            </p>

            <div className={styles.storeButtonsContainer}>
              <a
                href={iosUrl}
                className={styles.storeLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getAppStoreImage()}
                  alt="App Store"
                  className={styles.storeButton}
                />
              </a>

              <a
                href={androidUrl}
                className={styles.storeLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={getPlayStoreImage()}
                  alt="Play Store"
                  className={styles.storeButton}
                />
              </a>
            </div>

            <div className={styles.actionsContainer}>
              <button
                onClick={handleContinueOnWeb}
                className={styles.continueButton}
              >
                {translate("common.continueOnWeb")}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner} />
            <p className={styles.loadingText}>
              Redirection vers le store approprié...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
